import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import Divider from "@mui/material/Divider";
import { useCallback, useContext, useEffect, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import VehicleEntranceService from "./service/VehicleEntranceService";
import { formatTypeString } from "./utils";
import { useHasScope } from "../../../../config/globalConfig";
import useTenant from "../../hooks/useTenant";

interface FormInputs {
  internalDDT: string | null;
  externalDDT: string | null;
  plate: string | null;
  firstFueling: string | null;
}

const isError = (values: FormInputs) => {
  const { internalDDT, externalDDT, plate, firstFueling } = values;

  if (internalDDT) {
    if (externalDDT || plate || firstFueling) {
      return true;
    }

    return false;
  }

  if (externalDDT) {
    if (internalDDT || plate || firstFueling) {
      return true;
    }

    return false;
  }

  if (plate) {
    if (externalDDT || externalDDT || firstFueling) {
      return true;
    }

    return false;
  }

  if (firstFueling) {
    if (internalDDT || externalDDT || plate) {
      return true;
    }

    return false;
  }

  return true;
};

const VehicleEntranceSearch = () => {
  const hasScope = useHasScope;

  const navigate = useNavigate();

  const snackbar = useSnackbar();
  const t = useOperationTranslation();

  const [locations, setLocations] = useState([]);
  const { tenant, setLoading } = useContext(ContextOperation);

  const { TenantAutocomplete } = useTenant();

  const getData = useCallback(() => {
    setLoading(true);
    return VehicleEntranceService.get
      .garagesLocations(tenant)
      .then((response) => {
        setLoading(false);
        setLocations(response);
      });
  }, [tenant]);

  useEffect(() => {
    getData();
  }, [getData]);

  const resolver: Resolver<FormInputs> = async (values) => {
    return {
      values: !isError(values) ? values : {},
      errors: isError(values)
        ? {
            internalDDT: {
              type: "required",
              message: t("vehicleExit.status.fromErrorMessage"),
            },
          }
        : {},
    };
  };

  const form = useForm<FormInputs>({
    mode: "onChange",
    defaultValues: {
      internalDDT: "",
      externalDDT: "",
      plate: "",
      firstFueling: "",
    },
    resolver,
  });

  const onSubmit = (data: FormInputs) => {
    setLoading(true);
    const internalDDT = form.watch("internalDDT");
    const externalDDT = form.watch("externalDDT");
    const plate = form.watch("plate");
    const firstFueling = form.watch("firstFueling");

    if (internalDDT) {
      const url = "/operation/transfer/vehiclesEntrance/internalDDT";
      VehicleEntranceService.get
        .vehicleByDDT(tenant, data.internalDDT as string)
        .then(
          (response: any) => {
            setLoading(false);
            if (!response?.transferRequest.endDate) {
              navigate(url, {
                state: {
                  tenant: tenant,
                  ddt: data.internalDDT || data.externalDDT,
                  deliveryNoteNumber: data.internalDDT || data.externalDDT,
                  endDate: response?.transferRequest.endDate,
                  vehicles: response?.vehicles,
                  allLocations: locations.map((item: any) => {
                    return {
                      ...item,
                      label: `${item.locationEntityDesc} (${formatTypeString(
                        item.type,
                        t
                      )})`,
                      value: `${item.bpCode}-${item.type}`
                    };
                  }),
                  locationStart: locations.find(({bpCode, type}) => bpCode === response?.transferRequest.startStationId && response.transferRequest.startStationType === type) || null,
                  locationEnd: locations.find(({bpCode, type}) => bpCode === response?.transferRequest.expectedEndStationId && type === response.transferRequest.expectedEndStationType) || null,
                  drivers: response?.drivers,
                  referenceLocation: `${
                    response?.referenceLocationType === "LMS"
                      ? "DMS"
                      : response.referenceLocationType
                  } - ${response?.referenceLocationDescription}`,
                } as any,
              });
            } else {
              snackbar.show(
                "warning",
                t("vehicleEntrance.internalDDT.alreadyClosed").replace(
                  "#plateNotFound",
                  data.internalDDT || ""
                )
              );
            }
          },
          (reject) => {
            setLoading(false);
            snackbar.show(
              "warning",
              t("vehicleEntrance.search.notFound").replace(
                "#plateNotFound",
                data.internalDDT as string
              )
            );
          }
        );
    }

    if (externalDDT) {
      const url = "/operation/transfer/vehiclesEntrance/externalDDT";
      setLoading(false);
      navigate(url, {
        state: {
          tenant: tenant,
          ddt: data.externalDDT,
          deliveryNoteNumber: data.externalDDT,
          id: null,
          plate: null,
          modelType: null,
          status: null,
          endDate: null,
          vehicles: [],
          allLocations: locations.map((item: any) => {
            return {
              ...item,
              label: `${item.locationEntityDesc} (${formatTypeString(
                item.type,
                t
              )})`,
              value: `${item.bpCode}-${item.type}`
            };
          }),
          locationStart: null,
          locationEnd: null,
          drivers: [],
          fuel: null,
          kms: null,
          referenceLocation: null,
        },
      });
    }

    if (plate) {
      VehicleEntranceService.get
        .vehicleByPlateFirstCheckin(tenant, data.plate as string)
        .then(
          (response: any) => {
            const url =
              response.status === "FIRST_CHECKIN"
                ? "/operation/transfer/vehiclesEntrance/plate"
                : "/operation/transfer/vehiclesEntrance/plate/migration";

            setLoading(false);

            navigate(url, {
              state: {
                ...response.vehicle,
                allLocations: locations.map((item: any) => {
                  return {
                    ...item,
                    tenant: tenant,
                    label: `${item.locationEntityDesc} (${formatTypeString(
                      item.type,
                      t
                    )})`,
                    value: `${item.bpCode}-${item.type}`
                  };
                }),
              },
            });
          },
          (reject) => {
            setLoading(false);
            const status = reject.response.status;

            let error: any = { type: "error", message: "" };

            if (status === 409 && (reject.response.data.subCode === 13 || reject.response.data.subCode === 16)) {
              error = {
                type: "error",
                message: t(
                  "vehicleEntrance.search.errors.vehicleFieldsMissing"
                ),
              };
            } else if (status === 409 || status === 406) {
              error = {
                type: "warning",
                message: t("vehicleEntrance.search.errors.alreadyExist"),
              };
            }

            if (status === 404) {
              error = {
                type: "warning",
                message: t("vehicleEntrance.search.errors.notFound"),
              };
            }

            if (status === 400) {
              error = {
                type: "error",
                message: t("vehicleEntrance.search.errors.badRequest"),
              };
            }

            if (status === 500) {
              error = {
                type: "error",
                message: t("vehicleEntrance.search.errors.genericError"),
              };
            }

            snackbar.show(error.type, error.message);
          }
        );
    }

    if (firstFueling) {
      VehicleEntranceService.firstFueling.get.validateFirstFuelingByPlate(tenant, firstFueling).then(
        () => {
          navigate(`/operation/transfer/vehiclesEntrance/firstFueling/${firstFueling}`);
          setLoading(false);
        },
        (reject) => {
          const subCode = reject?.response.data.subCode;

            if (subCode === 15) {
              snackbar.show(
                "warning",
                t(
                  "vehicleEntrance.vehicleEntranceFirstFueling.snackbar.vehicleNotToBeFilled"
                )
              );
            }

            if (subCode === 2) {
              snackbar.show(
                "warning",
                t(
                  "vehicleEntrance.vehicleEntranceFirstFueling.snackbar.vehivleNotFound"
                )
              );
            }

            if (subCode === 3) {
              snackbar.show(
                "warning",
                t(
                  "vehicleEntrance.vehicleEntranceFirstFueling.snackbar.vehicleNotFleet"
                )
              );
            }

            if (subCode === 4) {
              snackbar.show(
                "warning",
                t(
                  "vehicleEntrance.vehicleEntranceFirstFueling.snackbar.vehicleAlreadyFilled"
                )
              );
            }

            if (subCode === 5) {
              snackbar.show(
                "warning",
                t(
                  "vehicleEntrance.vehicleEntranceFirstFueling.snackbar.termicElectricAbsent"
                )
              );
            }

            if (subCode === 6) {
              snackbar.show(
                "warning",
                t(
                  "vehicleEntrance.vehicleEntranceFirstFueling.snackbar.missingReceipt"
                )
              );
            }
            setLoading(false);
          }
        );
    }
  };

  return (
    <Mui.Box>
      <form id="vehicle-entrance-search" onSubmit={form.handleSubmit(onSubmit)}>
        <Mui.Box height="auto" padding={{ md: 2, xs: 0 }}>
          {TenantAutocomplete && (
            <Mui.Box marginBottom={2}>
              <Mui.Card>
                <Mui.CardContent>
                  <Mui.Grid container columns={3}>
                    {TenantAutocomplete}
                  </Mui.Grid>
                </Mui.CardContent>
              </Mui.Card>
            </Mui.Box>
          )}

          <Mui.Card style={{ height: "100%" }}>
            <Mui.CardContent style={{ height: "100%" }}>
              <Mui.Stack height="calc(100% - 32px)">
                <Mui.Typography variant="h4" textAlign="center" mb={2}>
                  {t("vehicleEntrance.search.content.title")}
                </Mui.Typography>

                <Mui.Typography
                  variant="body"
                  whiteSpace="pre-line"
                  textAlign="center"
                  mb="80px"
                >
                  {t("vehicleEntrance.search.content.description")}
                </Mui.Typography>

                <Mui.Box p="0px 16px" mb="25px">
                  <Mui.TextField
                    variant="standard"
                    label="Nr. DDT"
                    placeholder={t(
                      "vehicleEntrance.search.content.inputs.innerDocument.placeholder"
                    )}
                    {...form.register("internalDDT")}
                    value={form.watch("internalDDT") || ""}
                    fullWidth
                  />
                </Mui.Box>

                <Mui.Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  mb="25px"
                >
                  <Mui.Typography variant="body">
                    {t("vehicleEntrance.search.content.scanText")}
                  </Mui.Typography>
                  <MuiIcons.InfoOutlined style={{ marginLeft: "5px" }} />
                </Mui.Stack>

                <Mui.Button
                  variant="contained"
                  color="accent"
                  disabled={true}
                  style={{ margin: "0px 16px" }}
                >
                  {t("vehicleEntrance.search.content.buttonScan")}{" "}
                  <MuiIcons.QrCode />
                </Mui.Button>

                <Divider sx={{ my: 10 }} />

                <Mui.Box
                  p="0px 16px"
                  mb="25px"
                  sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Mui.Typography variant="h4" textAlign="center" mb={2}>
                    {t("vehicleEntrance.search.content.lowerTitle")}
                  </Mui.Typography>

                  <Mui.Typography
                    variant="body"
                    whiteSpace="pre-line"
                    textAlign="center"
                    mb={2}
                  >
                    {t("vehicleEntrance.search.content.lowerDescription")}
                  </Mui.Typography>
                  <Mui.TextField
                    variant="standard"
                    label={t(
                      "vehicleEntrance.search.content.inputs.outerDocument.label"
                    )}
                    placeholder={t(
                      "vehicleEntrance.search.content.inputs.outerDocument.placeholder"
                    )}
                    {...form.register("externalDDT")}
                    value={form.watch("externalDDT") || ""}
                    fullWidth
                  />
                </Mui.Box>

                <Divider sx={{ my: 10 }} />

                <Mui.Box
                  p="0px 16px"
                  mb="25px"
                  sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Mui.Typography variant="h4" textAlign="center" mb={2}>
                    {t("vehicleEntrance.search.plateSearch.content.lowerTitle")}
                  </Mui.Typography>

                  <Mui.Typography
                    variant="body"
                    whiteSpace="pre-line"
                    textAlign="center"
                    mb={2}
                  >
                    {t(
                      "vehicleEntrance.search.plateSearch.content.lowerDescription"
                    )}
                  </Mui.Typography>
                  <Mui.TextField
                    variant="standard"
                    label={t(
                      "vehicleEntrance.search.plateSearch.content.inputs.label"
                    )}
                    placeholder={t(
                      "vehicleEntrance.search.plateSearch.content.inputs.placeholder"
                    )}
                    {...form.register("plate")}
                    value={form.watch("plate") || ""}
                    fullWidth
                    helperText={t(
                      "vehicleEntrance.search.plateSearch.content.inputs.helperText"
                    )}
                  />
                </Mui.Box>

                {hasScope("SCOPE_UPSERT_MDVEHICLE_FIRSTFUELING") && (
                  <>
                    <Divider sx={{ my: 10 }} />

                    <Mui.Box
                      p="0px 16px"
                      mb="25px"
                      sx={{
                        justifyContent: "center",
                        alignItems: "center",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Mui.Typography variant="h4" textAlign="center" mb={2}>
                        {t(
                          "vehicleEntrance.search.firstRefueling.content.lowerTitle"
                        )}
                      </Mui.Typography>

                      <Mui.Typography
                        variant="body"
                        whiteSpace="pre-line"
                        textAlign="center"
                        mb={2}
                      >
                        {t(
                          "vehicleEntrance.search.firstRefueling.content.lowerDescription"
                        )}
                      </Mui.Typography>

                      <Mui.TextField
                        type="text"
                        label={t(
                          "vehicleEntrance.search.firstRefueling.content.inputs.label"
                        )}
                        placeholder={t(
                          "vehicleEntrance.search.firstRefueling.content.inputs.placeholder"
                        )}
                        fullWidth
                        {...form.register("firstFueling")}
                        value={form.watch("firstFueling")}
                      />
                    </Mui.Box>
                  </>
                )}
                <Mui.Button
                  variant="contained"
                  color="accent"
                  type="submit"
                  style={{ margin: "auto 16px 0px 16px" }}
                  disabled={!form.formState.isValid}
                >
                  {t("vehicleExit.search.content.buttonSearch")}
                </Mui.Button>
              </Mui.Stack>
            </Mui.CardContent>
          </Mui.Card>
        </Mui.Box>
      </form>
    </Mui.Box>
  );
};

export default VehicleEntranceSearch;
